import { FC } from 'react';
import type { RemediationItemCounts } from '@libs/types';
import { Box, type Theme, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { type SingularKeys, useTrans } from '@libs/i18n';

import { appliedCount } from '../utils/run-counts';

/**
 * One dot-and-count pair — "4 done", "1 failed".
 *
 * Colour is never the only carrier: every entry is labelled, so the breakdown
 * still reads correctly in greyscale and to anyone who cannot separate the
 * green from the amber.
 */
const OutcomeItem: FC<{ dot: string; label: string; emphasised?: boolean }> = observer(
  ({ dot, label, emphasised }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, backgroundColor: dot }} />
      <Typography
        variant="textXS_semiBold"
        sx={{ color: emphasised ? dot : 'text.secondary', fontSize: 11.5 }}
      >
        {label}
      </Typography>
    </Box>
  ),
);

/**
 * How a run's documents came out, shown under its status badge.
 *
 * The badge alone says a run happened, not how it went — "in progress" and
 * "completed" both hide whether anything is waiting on the user and whether
 * anything broke. These counts answer both without opening the run, which is the
 * whole reason a failed document was invisible from this table before.
 *
 * Zero-valued entries are dropped rather than rendered as "0 failed": a row of
 * zeroes reads as a problem list, and the absence of the entry says the same
 * thing more quietly.
 */
export const RunOutcomeBreakdown: FC<{ counts: RemediationItemCounts }> = observer(({ counts }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const entries: { key: string; dot: string; count: number; emphasised?: boolean }[] = [
    // Everything the AI settled that is not failed, not still in flight and not
    // "nothing to change" — see `appliedCount`, shared with the history view so
    // a row and its history cannot report different numbers.
    { key: 'done', dot: theme.palette.accent.green, count: appliedCount(counts) },
    { key: 'running', dot: theme.palette.accent.blue, count: counts.inFlight },
    {
      key: 'needsReview',
      dot: theme.palette.warning.main,
      count: counts.reviewable,
      emphasised: true,
    },
    { key: 'noChanges', dot: theme.palette.text.tertiary, count: counts.noChanges },
    { key: 'failed', dot: theme.palette.error.main, count: counts.failed, emphasised: true },
  ];

  const visible = entries.filter((entry) => entry.count > 0);
  if (visible.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem 0.75rem', mt: 0.5 }}>
      {visible.map((entry) => (
        <OutcomeItem
          key={entry.key}
          dot={entry.dot}
          emphasised={entry.emphasised}
          label={t(`app.remediation.list.outcome.${entry.key}` as SingularKeys, {
            count: entry.count,
          })}
        />
      ))}
    </Box>
  );
});

/**
 * The key, stated once under the table rather than repeated per row.
 *
 * The counts above are terse by necessity — "3 applied" has to fit in a cell —
 * so the meaning of each colour lives here instead of in a tooltip nobody opens.
 */
export const RunOutcomeLegend: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();

  const entries: { key: string; dot: (theme: Theme) => string }[] = [
    { key: 'done', dot: (t2) => t2.palette.accent.green },
    { key: 'needsReview', dot: (t2) => t2.palette.warning.main },
    { key: 'failed', dot: (t2) => t2.palette.error.main },
    { key: 'noChanges', dot: (t2) => t2.palette.text.tertiary },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.375rem 1.25rem',
        px: 2,
        py: 1.25,
        borderTop: `1px solid ${theme.palette.grey[200]}`,
        backgroundColor: theme.palette.grey[100],
        // Bled back out through `DataTable`'s footer padding so the strip spans
        // the card edge to edge, as a legend should — inset it would read as
        // another row rather than as the key to the column above.
        mx: -2,
        mt: -1.5,
        mb: 1.5,
      }}
    >
      {entries.map((entry) => (
        <OutcomeItem
          key={entry.key}
          dot={entry.dot(theme)}
          label={t(`app.remediation.list.legend.${entry.key}` as SingularKeys)}
        />
      ))}
    </Box>
  );
});
